import { useState } from "react";
import { MGButton } from "../../../../components";
import { FaFileCsv } from "react-icons/fa6";
import { hotelServiceRequestTypeToLabelMap, serviceRequestTypeToLabelMap } from "../../../../shared/utils";
import { EServiceRequestType } from "../../../../types/global.types";
import { IInvoiceTableRow } from "../../../../hooks";
import { EHotelServiceRequestType } from "../../../../constants";
import { Snackbar, Alert } from "@mui/material";

interface IExportInvoiceCSVProps {
  rows: Array<IInvoiceTableRow>;
}

interface ExportResult {
  success: boolean;
  totalRows: number;
  exportedRows: number;
  skippedRows: number;
  errorMessage?: string;
}

const createPadding = (n: number): string => String(n).padStart(2, "0");

const formatIsoToMMDDYYYY = (value?: string): string => {
  if (!value) return "";

  try {
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, yyyy, mm, dd] = match;
      return `${mm}/${dd}/${yyyy}`;
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";

    return `${createPadding(d.getMonth() + 1)}/${createPadding(d.getDate())}/${d.getFullYear()}`;
  } catch {
    return "";
  }
};

const escapeCsvValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const createCSV = (rows: Array<IInvoiceTableRow>) => {
  const headers = [
    "Expense",
    "Service Begin Date",
    "Service End Date",
    "Employee Id",
    "Unit Measure",
    "Trip",
    "Total",
  ];

  const lines = [
    headers.map(escapeCsvValue).join(","),
    ...rows.map((row) => {
      const scannedOn =
        row?.scannedByGreeterOn ||
        row?.updatedOn || "";

      const mmddyyyy = formatIsoToMMDDYYYY(scannedOn);
      const employeeId = row?.tmId || "";
      const requestType = serviceRequestTypeToLabelMap[row?.requestType as EServiceRequestType] 
      || hotelServiceRequestTypeToLabelMap[row?.requestType as EHotelServiceRequestType] 
      || row?.requestType || "";
      
      const values = [
        requestType,
        mmddyyyy,
        mmddyyyy,
        employeeId,
        "per Person",
        "",
        "",
      ];

      return values.map(escapeCsvValue).join(",");
    }),
  ];

  // excel compatibilty
  return `\uFEFF${lines.join("\r\n")}\r\n`;
};

const getFileName = () => {
  const d = new Date();
  const mmddyyyy = `${createPadding(d.getMonth() + 1)}-${createPadding(d.getDate())}-${d.getFullYear()}`;
  return `crew-scan-exports-[${mmddyyyy}].csv`;
};

export const ExportInvoiceCSV = ({ rows }: IExportInvoiceCSVProps) => {
  const handleExport = () => {
    const csv = createCSV(rows || []);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = getFileName();
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
  };

  return (
    <MGButton
      className="model-button"
      onClick={handleExport}
      disabled={!rows || rows.length === 0}
    >
      <FaFileCsv className="dollar-icon" />
      Export CSV
    </MGButton>
  );
};

